import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SavedPaymentMethodsFetchAPI } from "../../../../../api/SavedPaymentMethods";

/* Opcion "Guardar esta tarjeta" y lista de tarjetas guardadas (PanToken de PowerTranz).

   - Solo se monta para clientes con sesion y con PowerTranz seleccionado (lo decide Checkout.jsx); ademas el
     backend valida el JWT: un invitado nunca puede guardar aunque manipule el navegador.
   - Si la funcion esta apagada en el servidor (SAVED_CARDS_ENABLED) o la consulta falla, no renderiza nada:
     el checkout queda exactamente como antes.
   - La tarjeta se escribe unicamente en la pagina de PowerTranz. Aqui solo se ve marca y fecha; nunca el token. */
export default function SavedCardsPanel({ machineId, saveCard, onSaveCardChange }) {
  const { t, i18n } = useTranslation();
  const [state, setState] = useState({ loading: true, enabled: false, cards: [] });
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await SavedPaymentMethodsFetchAPI.list(machineId);
      setState({
        loading: false,
        enabled: res?.data?.enabled === true,
        cards: Array.isArray(res?.data?.data) ? res.data.data : [],
      });
    } catch (_) {
      // Sin sesion de cliente, funcion apagada o error de red: el panel simplemente no aparece
      setState({ loading: false, enabled: false, cards: [] });
    }
  }, [machineId]);

  useEffect(() => {
    load();
  }, [load]);

  // Si la funcion no esta disponible no se puede quedar marcada la opcion de guardar
  useEffect(() => {
    if (!state.loading && !state.enabled && saveCard) onSaveCardChange(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.loading, state.enabled]);

  const run = async (id, action) => {
    setBusyId(id);
    setError("");
    try {
      await action();
      await load();
    } catch (_) {
      setError(t("checkout.savedCardsError"));
    } finally {
      setBusyId(null);
    }
  };

  const labelOf = (card) => {
    const brand = card.brand || "Card";
    if (card.last4) return `${brand} •••• ${card.last4}`;
    const date = card.created_at ? new Date(card.created_at).toLocaleDateString(i18n.language) : "";
    return t("checkout.savedCardsSavedOn", { brand, date });
  };

  if (state.loading || !state.enabled) return null;

  const linkButton = {
    background: "none",
    border: "none",
    padding: 0,
    fontSize: ".78rem",
    color: "var(--text-3)",
    textDecoration: "underline",
    cursor: "pointer",
  };

  return (
    <div
      className="ck-saved-cards"
      style={{ marginTop: "1rem", padding: ".75rem 1rem", background: "var(--bg-2)", borderRadius: "var(--radius-sm)" }}
    >
      <label style={{ display: "flex", gap: ".6rem", alignItems: "flex-start", cursor: "pointer", margin: 0 }}>
        <input
          type="checkbox"
          checked={!!saveCard}
          onChange={(e) => onSaveCardChange(e.target.checked)}
          style={{ marginTop: ".25rem" }}
        />
        <span>
          <span style={{ display: "block", fontSize: ".9rem", fontWeight: 600, color: "var(--text)" }}>
            {t("checkout.saveCardOption")}
          </span>
          <span style={{ display: "block", fontSize: ".72rem", color: "var(--text-3)", marginTop: ".15rem" }}>
            {t("checkout.saveCardHint")}
          </span>
        </span>
      </label>

      {state.cards.length > 0 && (
        <div style={{ marginTop: ".9rem" }}>
          <p style={{ fontSize: ".8rem", fontWeight: 600, margin: "0 0 .4rem", color: "var(--text)" }}>
            {t("checkout.savedCardsTitle")}
          </p>
          {state.cards.map((card) => (
            <div
              key={card.id}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: ".5rem",
                padding: ".35rem 0",
                opacity: busyId === card.id ? 0.5 : 1,
              }}
            >
              <span style={{ fontSize: ".85rem", color: "var(--text)" }}>
                💳 {labelOf(card)}
                {card.is_default && (
                  <span style={{ marginLeft: ".4rem", fontSize: ".7rem", color: "var(--text-3)" }}>
                    · {t("checkout.savedCardsDefault")}
                  </span>
                )}
              </span>
              <span style={{ display: "flex", gap: ".75rem", whiteSpace: "nowrap" }}>
                {!card.is_default && (
                  <button
                    type="button"
                    style={linkButton}
                    disabled={busyId === card.id}
                    onClick={() => run(card.id, () => SavedPaymentMethodsFetchAPI.setDefault(card.id))}
                  >
                    {t("checkout.savedCardsMakeDefault")}
                  </button>
                )}
                <button
                  type="button"
                  style={linkButton}
                  disabled={busyId === card.id}
                  onClick={() => {
                    if (window.confirm(t("checkout.savedCardsRemoveConfirm"))) {
                      run(card.id, () => SavedPaymentMethodsFetchAPI.remove(card.id));
                    }
                  }}
                >
                  {t("checkout.savedCardsRemove")}
                </button>
              </span>
            </div>
          ))}
        </div>
      )}

      {error && <p style={{ fontSize: ".75rem", color: "#c0392b", margin: ".5rem 0 0" }}>{error}</p>}
    </div>
  );
}
